// hooks/useScheduledMessage.js

import { useState, useCallback, useEffect, useRef } from "react";
import {
  encryptWithGroupKey,
  getPublicKey,
  cachePublicKey,
  loadGroupKey,
  loadPrivateKey,
  decryptMessage,
  decryptObject,
  decryptWithGroupKey,
  decryptObjectWithGroupKey,
} from "../utils/Cryptoutils";
import {
  saveScheduledMessage,
  deleteScheduledMessage,
} from "../db/fsDB";

import { fetchAndCachePublicKey } from "../utils/pbKey";
import { encryptDualDm, encryptGroup } from "../utils/encrypt";

/* ─────────────────────────────────────────────────────────────────────────────
   INTERNAL HELPERS
───────────────────────────────────────────────────────────────────────────── */

function buildReplyPreview(selectedReply) {
  if (!selectedReply) return null;
  if (selectedReply.text) {
    return { type: "text", value: selectedReply.text.slice(0, 80) };
  }
  if (selectedReply.media) {
    return {
      type: "media",
      value: {
        url:      selectedReply.media.url      || null,
        mimeType: selectedReply.media.mimeType || null,
        fileName: selectedReply.media.fileName || null,
      },
    };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// updateLocalPreview — updates the chat-list sidebar preview for a chat
// ─────────────────────────────────────────────────────────────────────────────
const updateLocalPreview = (
  chatKey,
  previewText,
  now,
  scheduledAt,
  targetItem,
  setUsers,
  setGroups
) => {
  if (!chatKey) return;

  const lastMessage = {
    text:        previewText,
    isDeleted:   false,
    isScheduled: true,
    scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
  };

  if (targetItem.type === "user") {
    setUsers((prev) =>
      prev.map((u) =>
        String(u.chatId) === String(targetItem.chatId)
          ? { ...u, lastMessage, lastActivityAt: now }
          : u
      )
    );
  }
  if (targetItem.type === "group") {
    setGroups((prev) =>
      prev.map((g) =>
        String(g.groupId) === String(targetItem.groupId)
          ? { ...g, lastMessage, lastActivityAt: now }
          : g
      )
    );
  }
};

/* ─────────────────────────────────────────────────────────────────────────────
   HOOK
───────────────────────────────────────────────────────────────────────────── */

export const useScheduledMessage = ({
  socket,
  myChatId,
  selectedItem,
  setMessages,
  setUsers,
  setGroups,
  input,
  setInput,
  selectedReply,
  setSelectedReply,
  pendingMedia,
  setPendingMedia,
  voiceMediaRef,
  uploadMedia,
  clearPendingMedia,
  setIsUploading,
  setUploadProgress,
  groupMembers,
  getUserKey,
  getGroupKey,
  cryptoFns,
  showToast,
  scrollToBottom,
  uuid,

  initialScheduled = [],
}) => {
  const [isSending, setIsSending] = useState(false);

  const [scheduled, setScheduled] = useState(
    () => (initialScheduled ?? []).map((m) => ({
      ...m,
      id:         m.msgId || m.id,
      _scheduled: true,
    }))
  );
  const scheduledRef = useRef([]);
  scheduledRef.current = scheduled;

  /* ══════════════════════════════════════════════════════════════════════════
     REPLY PAYLOAD BUILDERS
  ══════════════════════════════════════════════════════════════════════════ */
  const buildDmReplyPayload = useCallback(
    async (reply, recipientPublicKey, senderPublicKey) => {
      if (!reply?.msgId) return null;
      const preview = buildReplyPreview(reply);
      let replyPreviewEncrypted       = null;
      let replyPreviewEncryptedSender = null;
      if (preview && recipientPublicKey && senderPublicKey) {
        const result = await encryptDualDm(
          JSON.stringify(preview),
          recipientPublicKey,
          senderPublicKey
        );
        if (result?.forRecipient) replyPreviewEncrypted       = JSON.stringify(result.forRecipient);
        if (result?.forSender)    replyPreviewEncryptedSender = JSON.stringify(result.forSender);
      }
      return {
        msgId: reply.msgId,
        replyPreviewEncrypted,
        replyPreviewEncryptedSender,
      };
    },
    []
  );

  const buildGroupReplyPayload = useCallback(async (reply, groupKey) => {
    if (!reply?.msgId) return null;
    const previewText = reply.text?.slice(0, 80) || "";
    let replyPreviewEncrypted = null;
    if (previewText && groupKey) {
      const enc = await encryptGroup(previewText, groupKey);
      if (enc) replyPreviewEncrypted = JSON.stringify(enc);
    }
    return { msgId: reply.msgId, replyPreviewEncrypted };
  }, []);

  /* ══════════════════════════════════════════════════════════════════════════
     LOCAL COUNTDOWN HELPERS
  ══════════════════════════════════════════════════════════════════════════ */
  const _addScheduled = useCallback((msgId, scheduledAt, type) => {
    const entry = { msgId, scheduledAt: new Date(scheduledAt), type };
    setScheduled((prev) => [...prev.filter((s) => s.msgId !== msgId), entry]);
  }, []);

  const _removeScheduled = useCallback((msgId) => {
    setScheduled((prev) => prev.filter((s) => s.msgId !== msgId));
  }, []);

  /* ══════════════════════════════════════════════════════════════════════════
     SOCKET EVENT LISTENERS
  ══════════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!socket) return;

    /* ──────────────────────────────────────────────────────────────────────
       FIX: schedule:new — fired on ALL sender devices.
       The originating device already has an optimistic record in state.
       Other (secondary) devices need to:
         1. Register the countdown timer  (_addScheduled — unchanged)
         2. Decrypt the encrypted payload and inject an optimistic record
            into `messages` state so the message appears immediately without
            requiring a page refresh.
    ────────────────────────────────────────────────────────────────────────*/
    const onNew = async ({
      msgId,
      scheduledAt,
      type,
      // ← NEW fields from server (may be absent on old server versions)
      recipientChatId,
      groupId,
      messageType,
      encryptedText,
      encryptedMedia,
      replyToMessageId,
      replyPreviewEncrypted,
      batchId,
    }) => {
      // Always register the timer (existing behaviour)
      _addScheduled(msgId, scheduledAt, type);

      // Only proceed with message injection when the server sent content
      // AND this device doesn't already have the message in state.
      if (!encryptedText && !encryptedMedia) return;

      try {
        const now = Date.now();
        const scheduledIso = new Date(scheduledAt).toISOString();

        if (type === "dm" && recipientChatId) {
          const chatKey = getUserKey(recipientChatId);

          // Guard: skip if this device already rendered the optimistic record
          // (the originating device; it built the record in scheduleMessageForItem)
          setMessages((prev) => {
            const list = prev[chatKey] || [];
            if (list.some((m) => m.id === msgId)) return prev; // already present

            // We can't call async inside setMessages; we'll inject after decryption
            return prev;
          });

          // ── Decrypt sender-copy envelope ────────────────────────────────
          let text  = null;
          let media = null;

          if (encryptedText?.ciphertext) {
            try {
              const myPrivateKey = await loadPrivateKey(myChatId);
              if (myPrivateKey) {
                let epk = encryptedText.ephemeralPublicKey;
                if (typeof epk === "string") {
                  try { epk = JSON.parse(epk); } catch { /* already raw */ }
                }
                text = await decryptMessage(
                  encryptedText.ciphertext,
                  encryptedText.iv,
                  epk,
                  myPrivateKey
                );
              }
            } catch {
              // text decrypt failed
            }
          }

          if (encryptedMedia?.ciphertext) {
            try {
              const myPrivateKey = await loadPrivateKey(myChatId);
              if (myPrivateKey) {
                let epk = encryptedMedia.ephemeralPublicKey;
                if (typeof epk === "string") {
                  try { epk = JSON.parse(epk); } catch { /* already raw */ }
                }
                media = await decryptObject(
                  encryptedMedia.ciphertext,
                  encryptedMedia.iv,
                  epk,
                  myPrivateKey
                );
              }
            } catch {
              // media decrypt failed
            }
          }

          // ── Decrypt reply preview ────────────────────────────────────────
          let replyPreview = null;
          if (replyPreviewEncrypted) {
            try {
              const myPrivateKey = await loadPrivateKey(myChatId);
              if (myPrivateKey) {
                const env = typeof replyPreviewEncrypted === "string"
                  ? JSON.parse(replyPreviewEncrypted)
                  : replyPreviewEncrypted;
                let epk = env.ephemeralPublicKey;
                if (typeof epk === "string") {
                  try { epk = JSON.parse(epk); } catch { /* already raw */ }
                }
                const plain = await decryptMessage(env.ciphertext, env.iv, epk, myPrivateKey);
                if (plain) {
                  try { replyPreview = JSON.parse(plain); }
                  catch { replyPreview = { type: "text", value: plain }; }
                }
              }
            } catch {
              // reply decrypt failed
            }
          }

          // ── Inject into messages state ───────────────────────────────────
          const optimisticRecord = {
            id:               msgId,
            chatKey,
            timestamp:        now,
            messageType:      messageType || (encryptedMedia ? "media" : "text"),
            text:             text  || null,
            media:            media || null,
            encryptedText:    encryptedText  ?? null,
            encryptedMedia:   encryptedMedia ?? null,
            replyToMessageId: replyToMessageId ?? null,
            replyPreview,
            fromMe:           true,
            senderChatId:     myChatId,
            recipientChatId,
            _optimistic:      true,
            _scheduled:       true,
            scheduledAt:      scheduledIso,
            batchId:          batchId ?? null,
          };

          setMessages((prev) => {
            const list = prev[chatKey] || [];
            if (list.some((m) => m.id === msgId)) return prev; // double-check
            return { ...prev, [chatKey]: [...list, optimisticRecord] };
          });

          // ── Update sidebar preview ───────────────────────────────────────
          const previewText = text || (media ? "📎 Media" : "");
          if (previewText) {
            setUsers((prev) =>
              prev.map((u) =>
                String(u.chatId) === String(recipientChatId)
                  ? {
                      ...u,
                      lastMessage: {
                        text:        previewText,
                        isDeleted:   false,
                        isScheduled: true,
                        scheduledAt: scheduledIso,
                      },
                      lastActivityAt: now,
                    }
                  : u
              )
            );
          }

          // ── Persist to IDB (so a hard refresh also works) ────────────────
          try {
            await saveScheduledMessage({
              msgId,
              chatKey,
              senderChatId:          myChatId,
              recipientChatId,
              fromMe:                true,
              messageType:           messageType || (encryptedMedia ? "media" : "text"),
              encryptedText:         encryptedText ? {
                ciphertext:         encryptedText.ciphertext,
                iv:                 encryptedText.iv,
                ephemeralPublicKey: encryptedText.ephemeralPublicKey ?? null,
              } : null,
              encryptedMedia:        encryptedMedia ? {
                ciphertext:         encryptedMedia.ciphertext,
                iv:                 encryptedMedia.iv,
                ephemeralPublicKey: encryptedMedia.ephemeralPublicKey ?? null,
              } : null,
              replyToMessageId:      replyToMessageId ?? null,
              replyPreviewEncrypted: replyPreviewEncrypted ?? null,
              scheduledAt:           scheduledIso,
              timestamp:             now,
              batchId:               batchId ?? null,
            });
          } catch {
            // IDB persist failed
          }

        } else if (type === "group" && groupId) {
          const chatKey = getGroupKey(groupId);

          // ── Decrypt using the group key ──────────────────────────────────
          let text  = null;
          let media = null;

          if (encryptedText?.ciphertext) {
            try {
              const groupKey = await loadGroupKey(groupId);
              if (groupKey) {
                text = await decryptWithGroupKey(
                  encryptedText.ciphertext,
                  encryptedText.iv,
                  groupKey
                );
              }
            } catch {
              // text decrypt failed
            }
          }

          if (encryptedMedia?.ciphertext) {
            try {
              const groupKey = await loadGroupKey(groupId);
              if (groupKey) {
                media = await decryptObjectWithGroupKey(
                  encryptedMedia.ciphertext,
                  encryptedMedia.iv,
                  groupKey
                );
              }
            } catch {
              // media decrypt failed
            }
          }

          // ── Decrypt reply preview ────────────────────────────────────────
          let replyPreview = null;
          if (replyPreviewEncrypted) {
            try {
              const groupKey = await loadGroupKey(groupId);
              if (groupKey) {
                const env = typeof replyPreviewEncrypted === "string"
                  ? JSON.parse(replyPreviewEncrypted)
                  : replyPreviewEncrypted;
                const plain = await decryptWithGroupKey(env.ciphertext, env.iv, groupKey);
                if (plain) {
                  try { replyPreview = JSON.parse(plain); }
                  catch { replyPreview = { type: "text", value: plain }; }
                }
              }
            } catch {
              // reply decrypt failed
            }
          }

          // ── Inject into messages state ───────────────────────────────────
          const optimisticRecord = {
            id:               msgId,
            chatKey,
            timestamp:        now,
            messageType:      messageType || (encryptedMedia ? "media" : "text"),
            text:             text  || null,
            media:            media || null,
            encryptedText:    encryptedText  ?? null,
            encryptedMedia:   encryptedMedia ?? null,
            replyToMessageId: replyToMessageId ?? null,
            replyPreview,
            isGroup:          true,
            groupId,
            fromMe:           true,
            senderChatId:     myChatId,
            _optimistic:      true,
            _scheduled:       true,
            scheduledAt:      scheduledIso,
            batchId:          batchId ?? null,
          };

          setMessages((prev) => {
            const list = prev[chatKey] || [];
            if (list.some((m) => m.id === msgId)) return prev;
            return { ...prev, [chatKey]: [...list, optimisticRecord] };
          });

          // ── Update sidebar preview ───────────────────────────────────────
          const previewText = text || (media ? "📎 Media" : "");
          if (previewText) {
            setGroups((prev) =>
              prev.map((g) =>
                String(g.groupId) === String(groupId)
                  ? {
                      ...g,
                      lastMessage: {
                        text:        previewText,
                        isDeleted:   false,
                        isScheduled: true,
                        scheduledAt: scheduledIso,
                      },
                      lastActivityAt: now,
                    }
                  : g
              )
            );
          }

          // ── Persist to IDB ────────────────────────────────────────────────
          try {
            await saveScheduledMessage({
              msgId,
              chatKey,
              senderChatId: myChatId,
              groupId,
              isGroup:      true,
              fromMe:       true,
              messageType:  messageType || (encryptedMedia ? "media" : "text"),
              encryptedText:  encryptedText ? {
                ciphertext:         encryptedText.ciphertext,
                iv:                 encryptedText.iv,
                ephemeralPublicKey: encryptedText.ephemeralPublicKey ?? null,
              } : null,
              encryptedMedia: encryptedMedia ? {
                ciphertext:         encryptedMedia.ciphertext,
                iv:                 encryptedMedia.iv,
                ephemeralPublicKey: encryptedMedia.ephemeralPublicKey ?? null,
              } : null,
              replyToMessageId:      replyToMessageId ?? null,
              replyPreviewEncrypted: replyPreviewEncrypted ?? null,
              scheduledAt:           scheduledIso,
              timestamp:             now,
              batchId:               batchId ?? null,
            });
          } catch {
            // IDB persist failed
          }
        }
      } catch {
        // multi-device inject failed
      }
    };

    const onSync = ({ msgId, scheduledAt }) => {
      _addScheduled(
        msgId,
        scheduledAt,
        scheduledRef.current.find((s) => s.msgId === msgId)?.type || "dm"
      );
    };

    const onUpdated = ({
      msgId,
      scheduledAt,
      newCiphertext,
      newIv,
      ciphertext,
      iv,
      ephemeralPublicKey,
    }) => {
      setScheduled((prev) =>
        prev.map((s) =>
          s.msgId === msgId ? { ...s, scheduledAt: new Date(scheduledAt) } : s
        )
      );

      setMessages((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].map((m) => {
            if (m.id !== msgId) return m;

            const update = { scheduledAt: new Date(scheduledAt).toISOString() };

            if (newCiphertext && newIv) {
              update.encryptedText = {
                ...(m.encryptedText || {}),
                ciphertext: newCiphertext,
                iv:         newIv,
              };
            }

            if (ciphertext && iv) {
              const existing = Array.isArray(m.encryptedText) ? m.encryptedText : [];
              update.encryptedText = existing.map((env) =>
                env.for === "sender"
                  ? {
                      ...env,
                      ciphertext,
                      iv,
                      ephemeralPublicKey: ephemeralPublicKey ?? env.ephemeralPublicKey,
                    }
                  : env
              );
            }

            return { ...m, ...update };
          });
        }
        return next;
      });
    };

    const onSent = ({ msgId, timestamp }) => {
      _removeScheduled(msgId);

      deleteScheduledMessage(msgId).catch(() => {});

      const realTs = timestamp ? new Date(timestamp).getTime() : null;
      setMessages((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          const idx = next[key].findIndex((m) => m.id === msgId);
          if (idx === -1) continue;
          next[key] = next[key].map((m) => {
            if (m.id !== msgId) return m;
            return {
              ...m,
              _scheduled:  false,
              scheduledAt: undefined,
              timestamp:   realTs ?? m.timestamp,
              _optimistic: true,
            };
          });
          break;
        }
        return next;
      });
    };

    const onStopTimer = ({ msgId }) => { _removeScheduled(msgId); };

    const onCancelled = ({ msgId }) => {
      _removeScheduled(msgId);

      deleteScheduledMessage(msgId).catch(() => {});

      setMessages((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].filter((m) => m.id !== msgId);
        }
        return next;
      });
    };

    socket.on("schedule:new",        onNew);
    socket.on("schedule:sync",       onSync);
    socket.on("schedule:updated",    onUpdated);
    socket.on("schedule:sent",       onSent);
    socket.on("schedule:stop-timer", onStopTimer);
    socket.on("schedule:cancelled",  onCancelled);

    return () => {
      socket.off("schedule:new",        onNew);
      socket.off("schedule:sync",       onSync);
      socket.off("schedule:updated",    onUpdated);
      socket.off("schedule:sent",       onSent);
      socket.off("schedule:stop-timer", onStopTimer);
      socket.off("schedule:cancelled",  onCancelled);
    };
  }, [socket, _addScheduled, _removeScheduled, setMessages, showToast, myChatId,
      getUserKey, getGroupKey, setUsers, setGroups]);

  /* ══════════════════════════════════════════════════════════════════════════
     PUBLIC: updateScheduled / cancelScheduled / formatTime
  ══════════════════════════════════════════════════════════════════════════ */
  const updateScheduled = useCallback(
    (msgId, newScheduledAt) => {
      if (!socket || !msgId || !newScheduledAt) return;
      socket.emit("schedule:update", {
        msgId,
        scheduledAt: new Date(newScheduledAt).toISOString(),
      });
    },
    [socket]
  );

  const cancelScheduled = useCallback(
    (msgId) => {
      if (!socket || !msgId) return;
      socket.emit("schedule:cancel", { msgId });
    },
    [socket]
  );

  const formatTime = useCallback((scheduledAt) => {
    const ms = new Date(scheduledAt) - Date.now();
    if (ms <= 0) return "Sending…";
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }, []);

  /* ══════════════════════════════════════════════════════════════════════════
     IDB HELPER — persist sender-copy envelope
  ══════════════════════════════════════════════════════════════════════════ */
  const _persistToIDB = useCallback(async ({
    msgId,
    chatKey,
    chatType,
    recipientChatId,
    groupId,
    scheduledAt,
    messageType,
    timestamp,
    senderTextEnv,
    senderMediaEnv,
    senderReplyPreview,
    replyToMessageId,
    batchId,
  }) => {
    try {
      const encryptedText = senderTextEnv?.ciphertext
        ? {
            ciphertext:         senderTextEnv.ciphertext,
            iv:                 senderTextEnv.iv,
            ephemeralPublicKey: senderTextEnv.ephemeralPublicKey ?? null,
          }
        : null;

      const encryptedMedia = senderMediaEnv?.ciphertext
        ? {
            ciphertext:         senderMediaEnv.ciphertext,
            iv:                 senderMediaEnv.iv,
            ephemeralPublicKey: senderMediaEnv.ephemeralPublicKey ?? null,
          }
        : null;

      const base = {
        msgId,
        chatKey,
        senderChatId:          myChatId,
        fromMe:                true,
        messageType,
        encryptedText,
        encryptedMedia,
        replyToMessageId:      replyToMessageId  ?? null,
        replyPreviewEncrypted: senderReplyPreview ?? null,
        scheduledAt,
        timestamp:             timestamp ?? Date.now(),
        batchId:               batchId   ?? null,
      };

      const record = chatType === "group"
        ? { ...base, groupId: groupId ?? null, isGroup: true }
        : { ...base, recipientChatId: recipientChatId ?? null };

      await saveScheduledMessage(record);
    } catch {
      // IDB persist failed
    }
  }, [myChatId]);

  /* ══════════════════════════════════════════════════════════════════════════
     CORE: scheduleMessageForItem
     NEW: accepts optional batchId for multi-recipient sends.
  ══════════════════════════════════════════════════════════════════════════ */
  const scheduleMessageForItem = useCallback(
    async ({
      textToSend,
      mediaFile      = null,
      scheduledAt,
      targetItem,
      replySnapshot  = null,
      mediaSnapshot  = null,
      clearAfter     = false,
      silent         = false,
      batchId        = null,
    }) => {
      if (!socket || !targetItem || !scheduledAt) return;

      const scheduledTime = new Date(scheduledAt);
      if (isNaN(scheduledTime) || scheduledTime <= new Date()) {
        showToast?.("Please pick a future date/time.", "error");
        return;
      }

      const isMediaSend = mediaFile instanceof File || mediaFile instanceof Blob;
      const text = typeof textToSend === "string" ? textToSend.trim() : "";
      if (!isMediaSend && !text) return;

      const msgId = uuid();
      const now   = Date.now();
      const replyPreview = buildReplyPreview(replySnapshot);

      const previewText = text || (isMediaSend ? "📎 Media" : "");

      try {
        /* ================================================================
           GROUP
        ================================================================ */
        if (targetItem.type === "group") {
          const groupId     = targetItem.groupId;
          const chatKey     = getGroupKey(groupId);
          const messageType = isMediaSend ? "media" : "text";

          const isVoice =
            mediaSnapshot?.isVoice === true ||
            mediaSnapshot?.mimeType?.startsWith("audio/");

          const resolvedType = mediaSnapshot?.mimeType?.startsWith("audio/")
            ? "audio"
            : mediaSnapshot?.mimeType?.startsWith("image/")
            ? "image"
            : mediaSnapshot?.mimeType?.startsWith("video/")
            ? "video"
            : "document";

          const optimisticMedia =
            isMediaSend && mediaSnapshot
              ? {
                  url:         mediaSnapshot.previewUrl ?? null,
                  type:        resolvedType,
                  mimeType:    mediaSnapshot.mimeType   ?? null,
                  fileName:    mediaSnapshot.fileName   ?? null,
                  fileSize:    mediaSnapshot.fileSize   ?? null,
                  isVoice,
                  _optimistic: true,
                }
              : null;

          const optimisticRecord = {
            id:               msgId,
            chatKey,
            timestamp:        now,
            messageType,
            text:             text || null,
            media:            optimisticMedia,
            replyToMessageId: replySnapshot?.msgId ?? null,
            replyPreview,
            isGroup:          true,
            groupId,
            fromMe:           true,
            senderChatId:     myChatId,
            _optimistic:      true,
            _scheduled:       true,
            scheduledAt:      scheduledTime.toISOString(),
            batchId:          batchId ?? null,
          };

          setMessages((prev) => ({
            ...prev,
            [chatKey]: [...(prev[chatKey] || []), optimisticRecord],
          }));

          // ── Update chat-list sidebar preview ──────────────────────────────
          updateLocalPreview(
            chatKey, previewText, now, scheduledTime,
            targetItem, setUsers, setGroups
          );

          if (clearAfter) {
            setInput?.("");
            setSelectedReply?.(null);
            clearPendingMedia?.();
            scrollToBottom?.();
          }

          const groupKey = await loadGroupKey(groupId);
          if (!groupKey) {
            showToast?.("Encryption key unavailable.", "error");
            setMessages((prev) => ({
              ...prev,
              [chatKey]: (prev[chatKey] || []).filter((m) => m.id !== msgId),
            }));
            return;
          }

          let encryptedText  = null;
          let encryptedMedia = null;
          let uploadedMedia  = null;

          if (text) {
            const enc = await encryptGroup(text, groupKey);
            if (!enc) {
              showToast?.("Failed to encrypt message.", "error");
              setMessages((prev) => ({
                ...prev,
                [chatKey]: (prev[chatKey] || []).filter((m) => m.id !== msgId),
              }));
              return;
            }
            encryptedText = { ciphertext: enc.ciphertext, iv: enc.iv, ephemeralPublicKey: null };
          }

          if (isMediaSend) {
            setIsUploading?.(true);
            try {
                          uploadedMedia = await uploadMedia(
                mediaFile,
                msgId,
                "message",
                (p) => setUploadProgress?.(p)
              );
            } finally {
              setIsUploading?.(false);
              setUploadProgress?.(0);
            }
            if (uploadedMedia) {
              const enc = await encryptGroup(JSON.stringify(uploadedMedia), groupKey);
              if (enc) {
                encryptedMedia = { ciphertext: enc.ciphertext, iv: enc.iv, ephemeralPublicKey: null };
              }
            }
          }

          if (!encryptedText && !encryptedMedia) {
            setMessages((prev) => ({
              ...prev,
              [chatKey]: (prev[chatKey] || []).filter((m) => m.id !== msgId),
            }));
            return;
          }

          const replyPayload = await buildGroupReplyPayload(replySnapshot, groupKey);

          if (isMediaSend && uploadedMedia) {
           const resolvedMedia = {
  url:
    uploadedMedia?.url ?? null,

  objectKey:
    uploadedMedia?.objectKey ?? null,

  encrypted:
    uploadedMedia?.encrypted ?? false,

  type:
    resolvedType,

  mimeType:
    mediaSnapshot?.mimeType ?? null,

  fileName:
    mediaSnapshot?.fileName ?? null,

  fileSize:
    mediaSnapshot?.fileSize ?? null,

  isVoice,
};
            setMessages((prev) => {
              const list = prev[chatKey] || [];
              return {
                ...prev,
                [chatKey]: list.map((m) => m.id === msgId ? { ...m, media: resolvedMedia } : m),
              };
            });
          }

          await _persistToIDB({
            msgId,
            chatKey,
            chatType:           "group",
            groupId,
            scheduledAt:        scheduledTime.toISOString(),
            messageType,
            timestamp:          now,
            senderTextEnv:      encryptedText,
            senderMediaEnv:     encryptedMedia,
            senderReplyPreview: replyPayload?.replyPreviewEncrypted ?? null,
            replyToMessageId:   replyPayload?.msgId ?? null,
            batchId,
          });

          socket.emit("schedule:create", {
            msgId,
            type: "group",
            groupId,
            messageType,
            ...(encryptedText  ? { encryptedText }  : {}),
            ...(encryptedMedia ? { encryptedMedia } : {}),
            ...(replyPayload
              ? {
                  replyToMessageId:      replyPayload.msgId,
                  replyPreviewEncrypted: replyPayload.replyPreviewEncrypted,
                }
              : {}),
            scheduledAt: scheduledTime.toISOString(),
            batchId:     batchId ?? undefined,
          });
        }

        /* ================================================================
           DM
        ================================================================ */
        if (targetItem.type === "user") {
          const chatKey     = getUserKey(targetItem.chatId);
          const messageType = isMediaSend ? "media" : "text";

          const isVoice =
            mediaSnapshot?.isVoice === true ||
            mediaSnapshot?.mimeType?.startsWith("audio/");

          const resolvedType = mediaSnapshot?.mimeType?.startsWith("audio/")
            ? "audio"
            : mediaSnapshot?.mimeType?.startsWith("image/")
            ? "image"
            : mediaSnapshot?.mimeType?.startsWith("video/")
            ? "video"
            : "document";

          const optimisticMedia =
            isMediaSend && mediaSnapshot
              ? {
                  url:         mediaSnapshot.previewUrl ?? null,
                  type:        resolvedType,
                  mimeType:    mediaSnapshot.mimeType   ?? null,
                  fileName:    mediaSnapshot.fileName   ?? null,
                  fileSize:    mediaSnapshot.fileSize   ?? null,
                  isVoice,
                  _optimistic: true,
                }
              : null;

          const optimisticRecord = {
            id:               msgId,
            chatKey,
            timestamp:        now,
            messageType,
            text:             text || null,
            media:            optimisticMedia,
            replyToMessageId: replySnapshot?.msgId ?? null,
            replyPreview,
            fromMe:           true,
            senderChatId:     myChatId,
            _optimistic:      true,
            _scheduled:       true,
            scheduledAt:      scheduledTime.toISOString(),
            batchId:          batchId ?? null,
          };

          setMessages((prev) => ({
            ...prev,
            [chatKey]: [...(prev[chatKey] || []), optimisticRecord],
          }));

          // ── Update chat-list sidebar preview ──────────────────────────────
          updateLocalPreview(
            chatKey, previewText, now, scheduledTime,
            targetItem, setUsers, setGroups
          );

          if (clearAfter) {
            setInput?.("");
            setSelectedReply?.(null);
            clearPendingMedia?.();
            scrollToBottom?.();
          }

          const [recipientPublicKey, senderPublicKey] = await Promise.all([
            fetchAndCachePublicKey(targetItem.chatId, socket),
            fetchAndCachePublicKey(myChatId,          socket),
          ]);

          if (!recipientPublicKey || !senderPublicKey) {
            showToast?.("Encryption keys unavailable.", "error");
            setMessages((prev) => ({
              ...prev,
              [chatKey]: (prev[chatKey] || []).filter((m) => m.id !== msgId),
            }));
            return;
          }

          let encryptedText           = null;
          let encryptedTextRecipient  = null;
          let encryptedMedia          = null;
          let encryptedMediaRecipient = null;
          let uploadedMedia           = null;

          if (text) {
            const result = await encryptDualDm(text, recipientPublicKey, senderPublicKey);
            if (!result) {
              showToast?.("Failed to encrypt message.", "error");
              setMessages((prev) => ({
                ...prev,
                [chatKey]: (prev[chatKey] || []).filter((m) => m.id !== msgId),
              }));
              return;
            }
            encryptedTextRecipient = result.forRecipient;
            encryptedText          = result.forSender;
          }

          if (isMediaSend) {
            setIsUploading?.(true);
            try {
                         uploadedMedia = await uploadMedia(
                mediaFile,
                msgId,
                "message",
                (p) => setUploadProgress?.(p)
              );
            } finally {
              setIsUploading?.(false);
              setUploadProgress?.(0);
            }
            if (uploadedMedia) {
              const result = await encryptDualDm(
                JSON.stringify(uploadedMedia),
                recipientPublicKey,
                senderPublicKey
              );
              if (!result) {
                showToast?.("Media encryption failed.", "error");
                setMessages((prev) => ({
                  ...prev,
                  [chatKey]: (prev[chatKey] || []).filter((m) => m.id !== msgId),
                }));
                return;
              }
              encryptedMediaRecipient = result.forRecipient;
              encryptedMedia          = result.forSender;
            }
          }

          const replyPayload = await buildDmReplyPayload(
            replySnapshot,
            recipientPublicKey,
            senderPublicKey
          );

          if (isMediaSend && uploadedMedia) {
           const resolvedMedia = {
  url:
    uploadedMedia?.url ?? null,

  objectKey:
    uploadedMedia?.objectKey ?? null,

  encrypted:
    uploadedMedia?.encrypted ?? false,

  type:
    resolvedType,

  mimeType:
    mediaSnapshot?.mimeType ?? null,

  fileName:
    mediaSnapshot?.fileName ?? null,

  fileSize:
    mediaSnapshot?.fileSize ?? null,

  isVoice,
};
            setMessages((prev) => {
              const list = prev[chatKey] || [];
              return {
                ...prev,
                [chatKey]: list.map((m) => m.id === msgId ? { ...m, media: resolvedMedia } : m),
              };
            });
          }

          await _persistToIDB({
            msgId,
            chatKey,
            chatType:           "dm",
            recipientChatId:    targetItem.chatId,
            scheduledAt:        scheduledTime.toISOString(),
            messageType,
            timestamp:          now,
            senderTextEnv:      encryptedText  ?? null,
            senderMediaEnv:     encryptedMedia ?? null,
            senderReplyPreview: replyPayload?.replyPreviewEncryptedSender ?? null,
            replyToMessageId:   replyPayload?.msgId ?? null,
            batchId,
          });

          socket.emit("schedule:create", {
            msgId,
            type:            "dm",
            recipientChatId: targetItem.chatId,
            messageType,

            ...(encryptedTextRecipient && encryptedText
              ? {
                  encryptedText: {
                    forRecipient: encryptedTextRecipient,
                    forSender:    encryptedText,
                  },
                }
              : {}),

            ...(encryptedMediaRecipient && encryptedMedia
              ? {
                  encryptedMedia: {
                    forRecipient: encryptedMediaRecipient,
                    forSender:    encryptedMedia,
                  },
                }
              : {}),

            ...(replyPayload?.replyPreviewEncrypted && replyPayload?.replyPreviewEncryptedSender
              ? {
                  replyToMessageId: replyPayload.msgId,
                  replyPreviewEncrypted: {
                    forRecipient: replyPayload.replyPreviewEncrypted,
                    forSender:    replyPayload.replyPreviewEncryptedSender,
                  },
                }
              : {}),

            scheduledAt: scheduledTime.toISOString(),
            batchId:     batchId ?? undefined,
          });
        }

      } catch {
        showToast?.("Failed to schedule message.", "error");
      }
    },
    [
      socket, myChatId,
      getUserKey, getGroupKey,
      buildDmReplyPayload, buildGroupReplyPayload,
      uploadMedia,
      showToast, scrollToBottom, uuid,
      setMessages, setUsers, setGroups,
      setInput, setSelectedReply, clearPendingMedia,
      setIsUploading, setUploadProgress,
      _persistToIDB,
    ]
  );

  /* ══════════════════════════════════════════════════════════════════════════
     CORE: scheduleMessage  (backward-compat wrapper — targets selectedItem)
  ══════════════════════════════════════════════════════════════════════════ */
   const scheduleMessage = useCallback(
    async (textOrFile, scheduledAt) => {
      if (!socket || !selectedItem || !scheduledAt) return;

      const isExplicitFile = textOrFile instanceof File || textOrFile instanceof Blob;

      // Multiple pending attachments (multi-file input) — schedule one
      // message per file, mirroring ChatInput's send loop.
      const mediaItems = !isExplicitFile && Array.isArray(pendingMedia)
        ? pendingMedia
        : [];

      setIsSending(true);
      try {
        if (mediaItems.length > 0) {
          const textToSend = typeof textOrFile === "string"
            ? textOrFile.trim()
            : (input ?? "").trim();

          for (let i = 0; i < mediaItems.length; i++) {
            await scheduleMessageForItem({
              textToSend:    i === 0 ? textToSend : "",
              mediaFile:     mediaItems[i]?.file ?? null,
              scheduledAt,
              targetItem:    selectedItem,
              replySnapshot: i === 0 ? selectedReply : null,
              mediaSnapshot: mediaItems[i],
              clearAfter:    i === 0,
            });
          }
        } else {
          const mediaSnapshot = isExplicitFile
            ? null
            : pendingMedia || voiceMediaRef?.current;
          const textToSend = isExplicitFile
            ? null
            : typeof textOrFile === "string"
              ? textOrFile.trim()
              : (input ?? "").trim();

          await scheduleMessageForItem({
            textToSend,
            mediaFile:     isExplicitFile ? textOrFile : null,
            scheduledAt,
            targetItem:    selectedItem,
            replySnapshot: selectedReply,
            mediaSnapshot,
            clearAfter:    true,
          });
        }
      } finally {
        setIsSending(false);
      }
    },
    [
      socket, selectedItem, input, selectedReply,
      pendingMedia, voiceMediaRef, scheduleMessageForItem,
    ]
  );

  return {
    scheduleMessage,
    scheduleMessageForItem,
    isSending,
    scheduled,
    updateScheduled,
    cancelScheduled,
    formatTime,
  };
};